import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { DataProvider } from './contexts/DataContext';
import { SettingsProvider } from './contexts/SettingsContext';
import MainLayout from './components/MainLayout';
import ImpersonationBar from './components/ImpersonationBar';
import Dashboard from './pages/Dashboard';
import KanbanBoard from './pages/KanbanBoard';
import AllLeads from './pages/AllLeads';
import AllNotifications from './pages/AllNotifications';
import FormBuilder from './pages/FormBuilder';
import PipelineManager from './pages/PipelineManager';
import LeadDetail from './components/LeadDetail';
import Statistics from './pages/Statistics';
import Services from './pages/Services';
import Deals from './pages/Deals';
import Empresas from './pages/Empresas';
import EmpresaDetail from './pages/EmpresaDetail';
import BusinessRules from './pages/BusinessRules';
import LoginPage from './pages/LoginPage';
import MagicLoginPage from './pages/MagicLoginPage';
import ReferidosPage from './pages/ReferidosPage';
import ReferidorDetail from './pages/ReferidorDetail';
import ReferidorPortal from './pages/ReferidorPortal';
import MisCampanas from './pages/MisCampanas';
import MiCampaignDetail from './pages/MiCampaignDetail';
// Campaigns pages
import CampaignsAdwords from './pages/CampaignsAdwords';
import CampaignDetail from './pages/CampaignDetail';
import CampaignsFacebook from './pages/CampaignsFacebook';
// Admin pages
import AdminSettings from './pages/admin/AdminSettings';
import AdminEmail from './pages/admin/AdminEmail';
import AdminAIIntegration from './pages/admin/AdminAIIntegration';
import AdminUsers from './pages/admin/AdminUsers';
import UserEdit from './pages/admin/UserEdit';
import AdminTracking from './pages/admin/AdminTracking';
import TrackingStatistics from './pages/admin/TrackingStatistics';
import ApiDocs from './pages/admin/ApiDocs';
import TraffickerPage from './pages/TraffickerPage';
import ComportamientosPage from './pages/ComportamientosPage';
import './index.css';

// Protected Route Component
const ProtectedRoute = ({ children, user, allowedRoles = null }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect referidores to their portal
    if (user.role === 'referidor') {
      return <Navigate to="/portal-referidos" replace />;
    }
    // Redirect traffickers to their section
    if (user.role === 'trafficker') {
      return <Navigate to="/trafficker" replace />;
    }
    return <Navigate to="/" replace />;
  }
  return children;
};

// Admin Only Route Component - redirects non-admins to home
const AdminOnlyRoute = ({ children, user }) => {
  const isAdmin = user?.role === 'admin' || user?.role === 'Administrador';
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in and verify with server
    const verifyUser = async () => {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (storedUser && token) {
        try {
          // Verify user from server to get updated role
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          } else {
            localStorage.removeItem('user');
            localStorage.removeItem('token');
          }
        } catch (e) {
          try {
            setUser(JSON.parse(storedUser));
          } catch {
            localStorage.removeItem('user');
            localStorage.removeItem('token');
          }
        }
      }
      setLoading(false);
    };
    verifyUser();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <SettingsProvider>
      <DataProvider>
        <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={
            user ? <Navigate to={user.role === 'referidor' ? '/portal-referidos' : '/'} replace /> : <LoginPage onLogin={handleLogin} />
          } />
          
          {/* Magic Link Login */}
          <Route path="/magic-login" element={
            <MagicLoginPage onLogin={handleLogin} />
          } />
          
          {/* Portal Referidos - Standalone page for referidores only */}
          <Route path="/portal-referidos" element={
            <ProtectedRoute user={user} allowedRoles={['referidor']}>
              <>
                <ImpersonationBar user={user} />
                <ReferidorPortal currentUser={user} onLogout={handleLogout} />
              </>
            </ProtectedRoute>
          } />
          
          {/* Main App Routes */}
          <Route path="/" element={
            <ProtectedRoute user={user} allowedRoles={['admin', 'comercial', 'asistente', 'viewer', 'trafficker']}>
              <>
                <ImpersonationBar user={user} />
                <MainLayout user={user} onLogout={handleLogout} />
              </>
            </ProtectedRoute>
          }>
            <Route index element={user?.role === 'trafficker' ? <Navigate to="/trafficker" replace /> : <Dashboard />} />
            <Route path="leads" element={<AllLeads />} />
            <Route path="notifications" element={<AllNotifications />} />
            {/* Admin-only: Pipeline Management */}
            <Route path="pipelines" element={
              <AdminOnlyRoute user={user}><PipelineManager /></AdminOnlyRoute>
            } />
            <Route path="kanban/:pipelineId" element={<KanbanBoard />} />
            <Route path="pipeline/:pipelineId" element={<KanbanBoard />} />
            <Route path="lead/:leadId" element={<LeadDetail />} />
            {/* Admin-only: Form Builder */}
            <Route path="form-builder" element={
              <AdminOnlyRoute user={user}><FormBuilder /></AdminOnlyRoute>
            } />
            <Route path="statistics" element={<Statistics />} />
            <Route path="services" element={<Services />} />
            <Route path="deals" element={<Deals />} />
            <Route path="trafficker" element={<TraffickerPage />} />
            <Route path="trafficker/comportamientos" element={<ComportamientosPage />} />
            <Route path="empresas" element={<Empresas />} />
            <Route path="empresa/:empresaId" element={<EmpresaDetail />} />
            {/* Admin-only: Referidos Management */}
            <Route path="referidos" element={
              <AdminOnlyRoute user={user}><ReferidosPage currentUser={user} isAdminView={true} /></AdminOnlyRoute>
            } />
            <Route path="referidos/:referidorId" element={
              <AdminOnlyRoute user={user}><ReferidorDetail /></AdminOnlyRoute>
            } />
            {/* Admin-only: Campaign Routes */}
            <Route path="campaigns/adwords" element={
              <AdminOnlyRoute user={user}><CampaignsAdwords /></AdminOnlyRoute>
            } />
            <Route path="campaigns/adwords/:campaignId" element={
              <AdminOnlyRoute user={user}><CampaignDetail /></AdminOnlyRoute>
            } />
            <Route path="campaigns/facebook" element={
              <AdminOnlyRoute user={user}><CampaignsFacebook /></AdminOnlyRoute>
            } />
            <Route path="campaigns/mis-campanas" element={
              <AdminOnlyRoute user={user}><MisCampanas /></AdminOnlyRoute>
            } />
            <Route path="campaigns/mis-campanas/:campaignId" element={
              <AdminOnlyRoute user={user}><MiCampaignDetail /></AdminOnlyRoute>
            } />
            {/* Admin Routes */}
            <Route path="admin/settings" element={
              <AdminOnlyRoute user={user}><AdminSettings /></AdminOnlyRoute>
            } />
            <Route path="admin/email" element={
              <AdminOnlyRoute user={user}><AdminEmail /></AdminOnlyRoute>
            } />
            <Route path="admin/ai-integration" element={
              <AdminOnlyRoute user={user}><AdminAIIntegration /></AdminOnlyRoute>
            } />
            {/* admin/users allows both admin and commercial (but commercial only sees own profile) */}
            <Route path="admin/users" element={<AdminUsers />} />
            <Route path="admin/users/:userId" element={<AdminOnlyRoute user={user}><UserEdit /></AdminOnlyRoute>} />
            <Route path="admin/tracking" element={
              <AdminOnlyRoute user={user}><AdminTracking /></AdminOnlyRoute>
            } />
            <Route path="admin/tracking-stats" element={
              <AdminOnlyRoute user={user}><TrackingStatistics /></AdminOnlyRoute>
            } />
            <Route path="admin/api-docs" element={
              <AdminOnlyRoute user={user}><ApiDocs /></AdminOnlyRoute>
            } />
            <Route path="admin/business-rules" element={
              <AdminOnlyRoute user={user}><BusinessRules /></AdminOnlyRoute>
            } />
          </Route>
        </Routes>
      </BrowserRouter>
    </DataProvider>
    </SettingsProvider>
  );
}

export default App;